import { create } from "zustand";
import { supabase } from "src/lib/supabaseClient";

export type CurriculumPlanSource = "published_scope" | "manual_selection" | "exam_blueprint";

export interface SubscriptionCurriculumConfig {
  plan_id: string;
  subscription_id: string;
  plan_source: CurriculumPlanSource;
  plan_status: "draft" | "active" | "superseded";
  curriculum_scope_id: string;
  objective_count: number;
  configured_by: string;
  configured_by_role: "student" | "admin";
  frozen_at: string | null;
}

interface SubscriptionCurriculumStore {
  configurations: Record<string, SubscriptionCurriculumConfig>;
  isLoading: boolean;
  error: string | null;
  fetchCurriculumConfig: (subscriptionId: string) => Promise<SubscriptionCurriculumConfig | null>;
  isSubscriptionConfigured: (subscriptionId: string) => boolean;
  getCurriculumConfig: (subscriptionId: string) => SubscriptionCurriculumConfig | null;
  resetState: () => void;
}

export const useSubscriptionCurriculumStore = create<SubscriptionCurriculumStore>((set, get) => ({
  configurations: {}, isLoading: false, error: null,
  fetchCurriculumConfig: async (subscriptionId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase.rpc("get_subscription_curriculum_config", { p_subscription_id: subscriptionId });
      if (error) throw error;
      const config = (data || [])[0] as SubscriptionCurriculumConfig | undefined;
      set((state) => ({ configurations: config ? { ...state.configurations, [subscriptionId]: config } : state.configurations, isLoading: false }));
      return config || null;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "Failed to load curriculum plan", isLoading: false });
      return null;
    }
  },
  isSubscriptionConfigured: (subscriptionId) => Boolean(get().configurations[subscriptionId]?.frozen_at),
  getCurriculumConfig: (subscriptionId) => get().configurations[subscriptionId] || null,
  resetState: () => set({ configurations: {}, isLoading: false, error: null }),
}));
